import React, { useState } from 'react';
import Editor from 'src/components/Editor/Editor';
import FileInput from 'src/components/File/File';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import CheckBox from 'src/components/Checkbox';
import Callout from 'src/components/InformationCallout/Callout';
import './ModernizedNoteForm.scss';

interface ImageFile extends File {
  preview: string;
}

interface Props {
  saving: boolean;
  onAddNote: (title: string, body: string, noteVisibleToHomeownerOccupantRealtor: boolean, image?: ImageFile | null) => Promise<void>;
  onEditNote?: (title: string, body: string, noteVisibleToHomeownerOccupantRealtor: boolean, image?: ImageFile | null, existingImageUrl?: string) => Promise<void>;
  initialNote?: any;
  onClose?: () => void;
  showHomeownerVisibilityCheckbox?: boolean;
  showImageUpload?: boolean;
  largeDescription?: boolean;
  addLabel?: string;
  editLabel?: string;
}

const DESCRIPTION_MIN_HEIGHT: number = 130;
const DESCRIPTION_MAX_HEIGHT: number = 220;

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

const ModernizedNoteForm: React.FC<Props> = ({
  saving,
  onAddNote,
  onEditNote,
  initialNote,
  onClose,
  showHomeownerVisibilityCheckbox = false,
  showImageUpload = true,
  largeDescription = false,
  addLabel = 'Save Note',
  editLabel = 'Save Changes',
}) => {
  const descriptionMinHeight: number = largeDescription
    ? DESCRIPTION_MIN_HEIGHT * 2
    : DESCRIPTION_MIN_HEIGHT;

  const descriptionMaxHeight: number = largeDescription
    ? DESCRIPTION_MAX_HEIGHT * 2
    : DESCRIPTION_MAX_HEIGHT;

  const [title, setTitle] = useState(initialNote?.title ?? '');
  const [body, setBody] = useState(initialNote?.body ?? '');
  const [image, setImage] = useState<ImageFile | null>(null);
  const [imageError, setImageError] = useState<string>('');
  const [visibleToResidents, setVisibleToResidents] = useState(initialNote?.noteVisibleToHomeownerOccupantRealtor ?? false);

  const isEditMode: boolean = !!initialNote;
  const existingImageUrl: string = initialNote?.image ?? '';

  const handleImageSelect = (files: ImageFile[]) => {
    const file:ImageFile = files[0] ?? null;

    if (file && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageError('Only PNG and JPG images are supported.');
      setImage(null);
      return;
    }

    setImageError('');
    setImage(file);
  };

  const handleSubmit = async () => {
    if (isEditMode && onEditNote) {
      await onEditNote(title, body, visibleToResidents, image, existingImageUrl);
    } else {
      await onAddNote(title, body, visibleToResidents, image);
    }

    setTitle('');
    setBody('');
    setImage(null);
    setVisibleToResidents(false);
    onClose?.();
  };

  const isBodyEmpty: boolean = !body.replace(/<(.|\n)*?>/g, '').trim();

  return (
    <div className="offline-sr-note-form">
      <p className="offline-sr-note-form-label">Subject</p>
      <input
        className="offline-sr-note-form-subject"
        type="text"
        placeholder="Subject"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <p className="offline-sr-note-form-label">Description</p>
      <Editor
        editorState={body}
        onEditorStateChange={setBody}
        placeholder="Leave a note or update for the team..."
        minHeight={descriptionMinHeight}
        maxHeight={descriptionMaxHeight}
      />
      {showImageUpload && (
      <div className="offline-sr-note-form-image-upload">
        {isEditMode && (existingImageUrl || image) && (
          <div className={`offline-sr-note-form-image-previews${image ? ' offline-sr-note-form-image-previews-two-up' : ''}`}>
            {existingImageUrl && (
              <div className="offline-sr-note-form-image-preview">
                <p className="offline-sr-note-form-image-heading">{image ? 'Current' : 'Attached Image'}</p>
                <img
                  src={existingImageUrl}
                  alt="Current attachment"
                  className="offline-sr-note-form-image-preview-img"
                />
              </div>
            )}
            {image && (
              <div className="offline-sr-note-form-image-preview">
                <p className="offline-sr-note-form-image-heading">New</p>
                <img
                  src={image.preview}
                  alt="New attachment"
                  className="offline-sr-note-form-image-preview-img"
                />
              </div>
            )}
          </div>
        )}
        <div className="offline-sr-note-form-image-dropzone">
          {!isEditMode && !image && (
            <p className="offline-sr-note-form-image-heading">Attach Image</p>
          )}
          <FileInput
            files={image ? [image] : []}
            onSelect={handleImageSelect}
            type="image"
            dropzoneLabel={isEditMode && existingImageUrl ? 'Replace Image' : undefined}
          />
          {imageError && (
            <Callout
              variant="error"
              title={imageError}
              className="offline-sr-note-form-image-error"
            />
          )}
        </div>
      </div>
      )}
      {showHomeownerVisibilityCheckbox && (
        <div className="input">
          <div>
            <CheckBox
              value={visibleToResidents}
              checked={visibleToResidents}
              label="Display note for homeowners, occupants, realtors"
              onChange={(e: any) => setVisibleToResidents(e.target.checked)}
            />
          </div>
        </div>
      )}
      <div className="offline-sr-note-form-action-row">
        <LoadingButton
          actionText={isEditMode ? editLabel : addLabel}
          onClick={handleSubmit}
          disabled={isBodyEmpty}
          isSubmitting={saving}
        />
      </div>
    </div>
  );
};

export default ModernizedNoteForm;
